import _get from 'lodash/get'
import { useContext } from '@nuxtjs/composition-api'
import useRequisites from './useRequisites'
import { capitalize } from '@/utils/generalUtils'

const DATA_SELECT_CONDITIONS = [
  'completed',
  'completedAllOf',
  'completedAnyOf',
  'completedAtLeastXOf',
  'enrolledIn',
  'minimumCredits',
  'minimumResidencyCredits',
  'minimumGrade',
  'averageGrade',
  'completeVariableCoursesAndVariableCredits',
]

function getVariableCreditCourseStatement(minValue, maxValue) {
  let statement = ''
  if (minValue && maxValue) {
    if (minValue === maxValue) {
      statement = `exactly ${minValue}`
    } else {
      statement = `${minValue} - ${maxValue}`
    }
  } else if (minValue && !maxValue) {
    statement = `${minValue} or more`
  } else if (!minValue && maxValue) {
    statement = `up to ${maxValue}`
  }
  return statement
}

export default {
  name: 'RequisitesSimpleRule',
  props: {
    rule: {
      type: Object,
      required: true,
    },
    relatedRequisites: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { root }) {
    const { $localize } = useContext()

    const { getRequisitesCreditsRangeLabel } = useRequisites({
      $axios: root.$axios,
      $store: root.$store,
      $localize,
    })

    return {
      getRequisitesCreditsRangeLabel,
    }
  },
  computed: {
    rules() {
      return this.rule.subRules || this.rule.rules
    },
    shouldShowDataSelect() {
      const valueCondition = _get(this.rule, 'value.condition', '')
      return (
        DATA_SELECT_CONDITIONS.includes(this.rule.condition) &&
        valueCondition !== 'none'
      )
    },
    displayString() {
      const valueCondition = _get(this.rule, 'value.condition')
      const getConditionDisplayString = (condition) => {
        if (condition === 'requisiteSets') {
          return 'Requirement Sets'
        }
        const localizedValue = this.$localize(condition)
        return capitalize(localizedValue, ' ')
      }
      switch (this.rule.condition) {
        case 'anyOf':
          return 'Fulfill ANY of the following requirements:'
        case 'allOf':
          return 'Fulfill ALL of the following requirements:'
        case 'numberOf':
          return `Complete at least ${this.rule.number} of the following:`
        case 'freeformText':
          return this.rule.value ? `${this.rule.value} ` : ''
        case 'minimumGPA':
          return `Earn a minimum GPA of ${this.rule.value}.`
        case 'minimumCredits': {
          const minimumCreditsString = `Earn at least ${
            this.rule.credits
          } ${this.$localize('credit', 'plural')}`
          if (valueCondition === 'none') return minimumCreditsString
          else return `${minimumCreditsString} from the following:`
        }
        case 'minimumResidencyCredits': {
          const minimumResidencyCreditsString = `Earn at least ${
            this.rule.credits
          } ${this.$localize('credit', 'plural')} in residence`
          if (valueCondition === 'none') return minimumResidencyCreditsString
          else return `${minimumResidencyCreditsString} from the following:`
        }
        case 'minimumGrade': {
          const minimumGradeString = `Earn a minimum ${this.rule.gradeType ||
            'grade'} of ${this.rule.grade}`
          if (valueCondition === 'none') return minimumGradeString
          else return `${minimumGradeString} in the following:`
        }
        case 'averageGrade': {
          const averageGradeString = `Earn an average ${this.rule.gradeType ||
            'grade'} of ${this.rule.grade}`
          if (valueCondition === 'none') return averageGradeString
          else return `${averageGradeString} in the following:`
        }
        case 'completed':
          return 'Complete the following:'
        case 'completedAllOf':
          return `Complete ALL of the following ${getConditionDisplayString(
            valueCondition
          )}:`
        case 'completedAnyOf':
          return `Complete ANY of the following ${getConditionDisplayString(
            valueCondition
          )}:`
        case 'completedAtLeastXOf':
          if (valueCondition === 'courseSets') {
            return `Complete at least ${this.rule.restriction ||
              0} courses in the following
              ${getConditionDisplayString(valueCondition)}:`
          } else {
            return `Complete at least ${this.rule.restriction ||
              0} of the following
            ${getConditionDisplayString(valueCondition)}:`
          }
        case 'enrolledIn':
          return `Enroll in the following ${getConditionDisplayString(
            valueCondition
          )}:`
        case 'completeVariableCoursesAndVariableCredits':
          const coursesString = getVariableCreditCourseStatement(
            this.rule.minCourses,
            this.rule.maxCourses
          )
          const creditsString = getVariableCreditCourseStatement(
            this.rule.minCredits,
            this.rule.maxCredits
          )
          return `Complete ${coursesString} course(s) and earn ${creditsString} ${this.$localize('credit(s)')} from the following:`
        default:
          return 'Requisite Condition Not Found'
      }
    },
  },
}
